import { STORAGE_KEYS } from '@/lib/constants/storage';
import type { SortDirection } from '@/lib/types/common';

export interface GridSortPreference<K extends string> {
  key: K;
  direction: SortDirection;
}

/**
 * Per-browser persistence of a grid's sort column + direction.
 * Keys are per feature (not per customer): sort is a user UI preference,
 * so it follows the browser across accounts.
 */
export class GridSortStorage {
  /** Pure validation of a raw stored payload (separated for unit tests). */
  static parse<K extends string>(
    raw: string | null,
    validKeys: readonly K[],
    fallback: GridSortPreference<K>,
  ): GridSortPreference<K> {
    if (!raw) return fallback;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null) return fallback;
      const { key, direction } = parsed as { key?: unknown; direction?: unknown };
      if (
        (validKeys as readonly unknown[]).includes(key) &&
        (direction === 'asc' || direction === 'desc')
      ) {
        return { key: key as K, direction };
      }
      return fallback;
    } catch {
      return fallback;
    }
  }

  static load<K extends string>(
    feature: string,
    validKeys: readonly K[],
    fallback: GridSortPreference<K>,
  ): GridSortPreference<K> {
    try {
      const raw = localStorage.getItem(`${STORAGE_KEYS.GRID_SORT}_${feature}`);
      return this.parse(raw, validKeys, fallback);
    } catch {
      return fallback;
    }
  }

  static save(feature: string, key: string, direction: SortDirection): void {
    try {
      localStorage.setItem(
        `${STORAGE_KEYS.GRID_SORT}_${feature}`,
        JSON.stringify({ key, direction }),
      );
    } catch {
      /* localStorage may be unavailable */
    }
  }
}
